/**
 * contactDisplay (client) — mirror of server/utils/contactDisplay.js.
 *
 * Single source of truth for resolving the name shown for a live-chat
 * conversation and whether it is a "saved" contact. Keeping this identical
 * to the server copy guarantees the same name appears whether it came from
 * an API fetch or a realtime socket update.
 */

const SAVED_SOURCES = new Set(["manual", "csv", "api"]);

function clean(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

export function isSavedContact(c) {
  if (!c) return false;
  const hasContact = !!(c.contact_id || c.id);
  if (!hasContact) return false;
  if (clean(c.first_name) || clean(c.last_name) || clean(c.full_name)) return true;
  return SAVED_SOURCES.has(c.source);
}

export function resolveContactDisplayName(input = {}) {
  const first = clean(input.first_name);
  const last = clean(input.last_name);
  const firstLast = [first, last].filter(Boolean).join(" ").trim();
  if (firstLast) return firstLast;

  const fullName = clean(input.full_name);
  if (fullName) return fullName;

  const customerName = clean(input.customer_name);
  if (customerName) return customerName;

  const whatsappName = clean(input.whatsapp_name);
  if (whatsappName) return whatsappName;

  return clean(input.phone) || clean(input.customer_phone) || "";
}

/**
 * Best-effort split of a WhatsApp id (full E.164 digits, no '+') into
 * { country_code, phone } where `phone` is the digits-only subscriber number.
 * Mirrors wabaserver/services/contactSync.splitWaId so a contact saved from
 * live chat lines up with the auto-synced master row (same unique key).
 */
export function splitWaId(waId) {
  const digits = String(waId || "").replace(/\D/g, "");
  if (!digits) return { country_code: "", phone: "" };

  const total = digits.length;
  if (total >= 11 && total <= 13) {
    const ccLen = total - 10;
    const cc = digits.slice(0, ccLen);
    const phone = digits.slice(ccLen);
    if (cc && phone.length === 10) return { country_code: cc, phone };
  }
  for (const ccLen of [1, 2, 3, 4]) {
    const cc = digits.slice(0, ccLen);
    const phone = digits.slice(ccLen);
    if (phone.length >= 6 && phone.length <= 12) {
      return { country_code: cc, phone };
    }
  }
  return { country_code: "", phone: digits };
}

export default { isSavedContact, resolveContactDisplayName, splitWaId };
